import { ChangeEvent, createContext, useContext } from 'react';

import type { AuthConnection, Region } from './hooks/useCreateAccount';
import type { AccountDetailsState } from './signupUtils';

export type SignupScreen =
  | 'loading'
  | 'verify_email'
  | 'under_review'
  | 'workspace_setup'
  | 'login_verify_email'
  | 'welcome';

type AccountDetailsContextValue = {
  accountDetails: AccountDetailsState;
  setField: (data: Partial<AccountDetailsState>) => void;
  handleRegionChange: (region: Region) => void;
};

type AuthConnectionsContextValue = {
  toggleAuthConnection: (
    connection: AuthConnection,
  ) => (e: ChangeEvent<{}>, checked: boolean) => void;
  hasSelectedAuthConnection: boolean;
  showAuthConnections: boolean;
  userAuthConnection: AuthConnection;
};

type WorkspaceNameFieldsContextValue = {
  loginId: string;
  handleDisplayNameChange: (value: string) => void;
  handleDisplayNameBlur: () => void;
  validDisplayName: boolean;
  validLoginId: boolean;
  helperText: string;
  loginIdAvailable: boolean;
  loginIdError?: string;
  loginIdConfirmedAvailable: boolean;
  isCheckingAvailability: boolean;
};

type CreateAccountFormContextValue = {
  screen: SignupScreen;
  error?: Error;
  isAccountCreated: boolean;
  showOverlay: boolean;
  canSubmit: boolean;
  acceptMarketingEmails: boolean;
  setAcceptMarketingEmails: (value: boolean) => void;
  handleLogin: () => Promise<void>;
  handleCreate: () => void;
  handleResultDismissed: () => void;
  recheckEmailVerification: () => void;
};

type CreateAccountFormProviderValue = AccountDetailsContextValue &
  AuthConnectionsContextValue &
  WorkspaceNameFieldsContextValue &
  CreateAccountFormContextValue;

export const CreateAccountFormContext =
  createContext<CreateAccountFormProviderValue | null>(null);

const useFormContext = (): CreateAccountFormProviderValue => {
  const ctx = useContext(CreateAccountFormContext);
  if (!ctx) {
    throw new Error(
      'CreateAccountForm context hooks must be used within CreateAccountFormProvider',
    );
  }
  return ctx;
};

export const useAccountDetailsContext = (): AccountDetailsContextValue =>
  useFormContext();

export const useAuthConnectionsContext = (): AuthConnectionsContextValue =>
  useFormContext();

export const useWorkspaceNameFieldsContext =
  (): WorkspaceNameFieldsContextValue => useFormContext();

export const useCreateAccountFormContext = (): CreateAccountFormContextValue =>
  useFormContext();
